import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { fmtPrice, fmtTime, RunnerMark, BookLogo, Segmented } from './racingShared'
import './FlucsModal.css'

// Full price history for one runner, opened from the desktop Flucs column:
// the median across all books, plus the ONE bookmaker hovered or pinned in the
// list (kept to two lines on purpose: every book at once was a tangle), on a
// real time axis. The price scale fits whatever is showing.
// `compact` (mobile): a full-screen sheet with a smaller chart box so the text
// stays readable, drag a finger across the chart to read it, tap a bookmaker
// to chart it. Fed live from RaceBoard (runner.moves / flucLine from
// buildRunners), so it keeps moving while open.

const RANGES = [
  { id: 'all', label: 'All' },
  { id: '1h', label: '1 hour' },
  { id: '15m', label: '15 min' },
]
const RANGE_MS = { '1h': 60 * 60 * 1000, '15m': 15 * 60 * 1000 }

// chart box (viewBox units; the svg scales to the modal's width). Price labels on the right.
const SIZES = {
  full: { W: 720, H: 320, PAD: { top: 14, right: 48, bottom: 26, left: 10 }, timeTicks: 6 },
  compact: { W: 360, H: 230, PAD: { top: 12, right: 40, bottom: 24, left: 6 }, timeTicks: 4 },
}

const MIN = 60 * 1000
const TIME_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720].map((m) => m * MIN)
const PRICE_STEPS = [0.01, 0.02, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100]

// A move list's price at time t: the last move at or before it.
const priceAt = (moves, t) => {
  let p = null
  for (const m of moves) {
    if (m.t > t) break
    p = m.price
  }
  return p
}
// The smallest step that gives at most `max` ticks over `span`.
const niceStep = (span, steps, max) => steps.find((s) => span / s <= max) ?? steps[steps.length - 1]
const pct = (from, to) => Math.round((to / from - 1) * 100)
const dirOf = (from, to) => (to > from ? 'up' : to < from ? 'down' : 'flat')

export default function FlucsModal({ race, runner, books, code, market, now, onClose, compact = false }) {
  const { W, H, PAD, timeTicks: maxTimeTicks } = compact ? SIZES.compact : SIZES.full
  const PLOT_W = W - PAD.left - PAD.right
  const PLOT_H = H - PAD.top - PAD.bottom
  const [range, setRange] = useState('all')
  const [hoverBook, setHoverBook] = useState(null)
  const [pinned, setPinned] = useState(null)
  const [cursor, setCursor] = useState(null)   // { t, py }: time and height (viewBox units) under the pointer
  const svgRef = useRef(null)

  // The page re-renders every second, so onClose is pinned in a ref and this
  // runs once per opening (as the mobile meetings sheet does).
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') closeRef.current() }
    document.addEventListener('keydown', esc)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', esc)
      document.body.style.overflow = prevOverflow
    }
  }, [])

  const median = runner.flucLine || []
  const lines = books
    .map((book, i) => ({ book, moves: runner.moves?.[i] || [] }))
    .filter((l) => l.moves.length)
  const focus = hoverBook ?? pinned
  const focusLine = lines.find((l) => l.book === focus) || null
  const shown = [{ book: null, moves: median }, ...(focusLine ? [focusLine] : [])]

  // ---- scales ----
  const firsts = [median, ...lines.map((l) => l.moves)].filter((m) => m.length).map((m) => m[0].t)
  const earliest = firsts.length ? Math.min(...firsts) : now
  const t1 = Math.max(now, earliest + MIN)
  const t0 = range === 'all' ? earliest : Math.max(earliest, t1 - RANGE_MS[range])
  const inView = (moves) => {
    const v = []
    const start = priceAt(moves, t0)
    if (start != null) v.push(start)
    for (const m of moves) if (m.t > t0) v.push(m.price)
    return v
  }
  const seen = shown.flatMap((l) => inView(l.moves))
  let lo = seen.length ? Math.min(...seen) : 1
  let hi = seen.length ? Math.max(...seen) : 2
  if (hi - lo < 0.1) {
    lo -= 0.05
    hi += 0.05
  }
  const room = (hi - lo) * 0.08
  lo -= room
  hi += room
  const x = (t) => PAD.left + ((t - t0) / (t1 - t0)) * PLOT_W
  const y = (p) => PAD.top + (1 - (p - lo) / (hi - lo)) * PLOT_H

  // Step path of a move list across the window (a price holds until it moves), run on to now.
  const path = (moves) => {
    let rest = moves.filter((m) => m.t > t0)
    const start = priceAt(moves, t0)
    let d
    if (start != null) d = `M${x(t0).toFixed(1)} ${y(start).toFixed(1)}`
    else if (rest.length) {
      d = `M${x(rest[0].t).toFixed(1)} ${y(rest[0].price).toFixed(1)}`
      rest = rest.slice(1)
    } else return null
    for (const m of rest) d += ` H${x(m.t).toFixed(1)} V${y(m.price).toFixed(1)}`
    return `${d} H${x(t1).toFixed(1)}`
  }

  const priceStep = niceStep(hi - lo, PRICE_STEPS, 5)
  const priceTicks = []
  for (let k = Math.ceil(lo / priceStep); k * priceStep <= hi; k++) priceTicks.push(+(k * priceStep).toFixed(2))
  const timeStep = niceStep(t1 - t0, TIME_STEPS, maxTimeTicks)
  const timeTicks = []
  for (let t = Math.ceil(t0 / timeStep) * timeStep; t <= t1; t += timeStep) timeTicks.push(t)
  const showJump = race.start > t0 && race.start < t1

  const onMove = (e) => {
    const r = svgRef.current.getBoundingClientRect()
    const px = ((e.clientX - r.left) / r.width) * W
    if (px < PAD.left || px > W - PAD.right) return setCursor(null)
    setCursor({ t: t0 + ((px - PAD.left) / PLOT_W) * (t1 - t0), py: ((e.clientY - r.top) / r.height) * H })
  }


  // ---- summary ----
  const opened = median[0]?.price
  const current = median[median.length - 1]?.price
  const medianDir = opened != null && current != null ? dirOf(opened, current) : 'flat'
  const readT = cursor?.t ?? now
  const readMedian = priceAt(median, readT)
  const readFocus = focusLine ? priceAt(focusLine.moves, readT) : null

  const rows = lines
    .map((l) => ({ book: l.book, open: l.moves[0].price, now: l.moves[l.moves.length - 1].price }))
    .sort((a, b) => b.now - a.now || a.book.localeCompare(b.book))
  const best = rows[0]?.now

  return createPortal(
    <div className={`rc-portal rc-fm-backdrop${compact ? ' is-compact' : ''}`} onClick={onClose}>
      <div className={`rc-fm${compact ? ' is-compact' : ''}`} role="dialog" aria-modal="true" aria-label={`Price history, ${runner.name}`} onClick={(e) => e.stopPropagation()}>
        <header className="rc-fm-head">
          <RunnerMark number={runner.number} silk={runner.silk} code={code} />
          <div className="rc-fm-who">
            <h2 className="rc-fm-name">{runner.number}. {runner.name}</h2>
            <span className="rc-fm-race">{race.track} R{race.race_number} · {fmtTime(race.start)} · {market === 'win' ? 'Win' : 'Place'}</span>
          </div>
          {opened != null && (
            <div className="rc-fm-stats">
              <span className="rc-fm-stat"><span className="rc-fm-stat-label">Opened</span>{fmtPrice(opened)}</span>
              <span className="rc-fm-stat"><span className="rc-fm-stat-label">Now</span><span className={`is-${medianDir}`}>{fmtPrice(current)}</span></span>
              <span className="rc-fm-stat">
                <span className="rc-fm-stat-label">Move</span>
                <span className={`is-${medianDir}`}>{pct(opened, current) > 0 ? '+' : ''}{pct(opened, current)}%</span>
              </span>
            </div>
          )}
          <button className="rc-fm-close" onClick={onClose} aria-label="Close" autoFocus>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </header>

        <div className="rc-fm-body">
          <section className="rc-fm-chart-wrap">
            <div className="rc-fm-bar">
              <Segmented label="Time range" options={RANGES} value={range} onChange={setRange} />
              <div className="rc-fm-read">
                <span className="rc-fm-read-time">{cursor == null ? 'Now' : fmtTime(cursor.t)}</span>
                <span className="rc-fm-key"><i className={`rc-fm-swatch is-median is-${medianDir}`} />Median {readMedian == null ? '–' : fmtPrice(readMedian)}</span>
                {focusLine && (
                  <span className="rc-fm-key"><i className="rc-fm-swatch is-focus" />{focusLine.book} {readFocus == null ? '–' : fmtPrice(readFocus)}</span>
                )}
              </div>
            </div>

            <div className="rc-fm-plot">
            <svg
              ref={svgRef}
              className="rc-fm-chart"
              viewBox={`0 0 ${W} ${H}`}
              onPointerDown={onMove}
              onPointerMove={onMove}
              onPointerLeave={() => setCursor(null)}
            >
              {priceTicks.map((p) => (
                <g key={p}>
                  <line className="rc-fm-grid" x1={PAD.left} x2={W - PAD.right} y1={y(p)} y2={y(p)} />
                  <text className="rc-fm-tick" x={W - PAD.right + 8} y={y(p) + 3.5}>{fmtPrice(p)}</text>
                </g>
              ))}
              {timeTicks.map((t) => (
                <text key={t} className="rc-fm-tick" x={x(t)} y={H - 8} textAnchor="middle">{fmtTime(t)}</text>
              ))}
              {showJump && (
                <g>
                  <line className="rc-fm-jump" x1={x(race.start)} x2={x(race.start)} y1={PAD.top} y2={H - PAD.bottom} />
                  <text className="rc-fm-jump-label" x={x(race.start) + 4} y={PAD.top + 9}>Jump</text>
                </g>
              )}

              {focusLine && <path className="rc-fm-book is-focus" d={path(focusLine.moves) || undefined} />}
              {median.length > 0 && <path className={`rc-fm-median is-${medianDir}`} d={path(median) || undefined} />}
              {current != null && <circle className={`rc-fm-end is-${medianDir}`} cx={x(t1)} cy={y(current)} r="3.5" />}

              {cursor != null && (
                <g>
                  <line className="rc-fm-cursor" x1={x(cursor.t)} x2={x(cursor.t)} y1={PAD.top} y2={H - PAD.bottom} />
                  {readMedian != null && <circle className={`rc-fm-end is-${medianDir}`} cx={x(cursor.t)} cy={y(readMedian)} r="3" />}
                  {readFocus != null && <circle className="rc-fm-end is-focus" cx={x(cursor.t)} cy={y(readFocus)} r="3" />}
                </g>
              )}
            </svg>
            {/* Prices at the pointer's time, wherever it is up or down the chart */}
            {/* (not on a phone: the finger would cover it; the bar above reads out instead) */}
            {!compact && cursor && (readMedian != null || readFocus != null) && (
              <div
                className={`rc-fm-pop${x(cursor.t) > W * 0.7 ? ' is-left' : ''}`}
                style={{ left: `${(x(cursor.t) / W) * 100}%`, top: `${(Math.min(Math.max(cursor.py, PAD.top + 24), H - PAD.bottom - 24) / H) * 100}%` }}
              >
                <span className="rc-fm-pop-time">{fmtTime(cursor.t)}</span>
                {readMedian != null && (
                  <span className="rc-fm-pop-row">
                    <span className="rc-fm-pop-who"><i className={`rc-fm-swatch is-median is-${medianDir}`} />Median</span>
                    <span className="rc-fm-pop-price">{fmtPrice(readMedian)}</span>
                  </span>
                )}
                {focusLine && readFocus != null && (
                  <span className="rc-fm-pop-row">
                    <span className="rc-fm-pop-who"><BookLogo book={focusLine.book} size={16} />{focusLine.book}</span>
                    <span className="rc-fm-pop-price">{fmtPrice(readFocus)}</span>
                  </span>
                )}
              </div>
            )}
            </div>
          </section>

          <aside className="rc-fm-books" aria-label="Bookmakers">
            <div className="rc-fm-books-head">
              <span>Bookmaker</span>
              <span>Open</span>
              <span>Now</span>
            </div>
            <div className="rc-fm-books-list" onMouseLeave={compact ? undefined : () => setHoverBook(null)}>
              {rows.map((r) => {
                const dir = dirOf(r.open, r.now)
                const cls = ['rc-fm-row', r.book === focus && 'is-focus', r.book === pinned && 'is-pinned'].filter(Boolean).join(' ')
                return (
                  <button
                    key={r.book}
                    className={cls}
                    onMouseEnter={compact ? undefined : () => setHoverBook(r.book)}
                    onClick={() => setPinned((p) => (p === r.book ? null : r.book))}
                    aria-pressed={r.book === pinned}
                  >
                    <span className="rc-fm-row-book"><BookLogo book={r.book} size={20} />{r.book}</span>
                    <span className="rc-fm-row-open">{fmtPrice(r.open)}</span>
                    <span className={`rc-fm-row-now is-${dir}${r.now === best ? ' is-best' : ''}`}>{fmtPrice(r.now)}</span>
                  </button>
                )
              })}
            </div>
            <p className="rc-fm-hint">
              {compact ? 'Tap a bookmaker to chart it against the median.' : 'Hover a bookmaker to chart it against the median. Click to keep it.'}
            </p>
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  )
}
